import React, {useEffect, useState} from 'react';
import './Quote.scss';
import {Booking, QuoteObj, TermsOfSale, TransportMethod, bookingActions} from '../../../../features/bookingSlice';
import {AMIPopover, Icon, AMIModal, AMIButton, AMITable} from '../../../../ui-components/UiComponents';
import {
    OptionalCharge,
    calculateBasePrice,
    getCarrierName,
    getECSData,
    getOptionalChargeCost,
    removeUnderscoresFromString,
    snakeCaseConvertor
} from '../../../../utilities/HelperUtilities';
import {calculateQuoteCost, showTime} from '../utilities';
import dayjs from 'dayjs';
import {useAppDispatch, useAppSelector} from '../../../../app/hooks';
import {customerDetailsSelector} from '../../../../features/customerSlice';
import {PageStatus, appDataActions, appDataSelector} from '../../../../features/appDataSlice';
import {useHistory} from 'react-router-dom';
import {TradeRoute, getTradeRoute} from '../../../../utilities/RulesEngineUtilities';

const Quote: React.FC<{
    quote: QuoteObj
    insured: boolean
    ddpSelected: boolean
    insuranceCost: number
    showServiceType: boolean
    booking: Booking
}> = ({
          quote,
          insured,
          ddpSelected,
          insuranceCost,
          showServiceType,
          booking
      }) => {

    const dispatch = useAppDispatch();
    const customer = useAppSelector(customerDetailsSelector);
    const ddpCost = getOptionalChargeCost(quote, OptionalCharge.DELIVERED_DUTY_PAID);
    const currencySymbol = customer.countryOfResidence.currencySymbol;
    const history = useHistory();
    const {isWl} = useAppSelector(appDataSelector);

    const [isRestrictionsPopoverVisible, setIsRestrictionsPopoverVisible] = useState(false);
    const [deliveryDate, setDeliveryDate] = useState('');
    const [quoteCost, setQuoteCost] = useState<any>();
    const [ecsData, setECSData] = useState<any>({type: "", amount: ""});
    const [basePrice, setBasePrice] = useState<number>(0);

    useEffect(() => {
        getAndSetDeliveryDate();
        setECSData(getECSData(booking));
        setBasePrice(calculateBasePrice(customer, quote))
    }, [])

    useEffect(() => {
        const totalCost = calculateQuoteCost(
          quote,
          booking,
          customer,
          insured,
          insuranceCost,
          ddpSelected,
          ddpCost
        );

        setQuoteCost(currencySymbol + totalCost);
    }, [insured, ddpSelected]);

    const getAndSetDeliveryDate = () => {
        const SUNDAY = 0;
        const SATURDAY = 6
        let deliveryDate = dayjs(quote.deliveryDateEstimate);

        if (deliveryDate.day() === SATURDAY) deliveryDate = deliveryDate.add(2, 'day');
        if (deliveryDate.day() === SUNDAY) deliveryDate = deliveryDate.add(1, 'day');

        setDeliveryDate(deliveryDate.format("ddd DD MMM"));
    }

    const formatTime = () => {
        if (showTime(quote)) return dayjs(quote.deliveryDateEstimate).format('hh:mmA')
        else return ''
    }

    const setCollectionTimes = (quote: QuoteObj) => {
        if (quote.pickupEarliestTime) {
            dispatch(bookingActions.updateCollectionDetail({
                field: 'pickUpFrom',
                value: quote.pickupEarliestTime
            }))
        }

        if (quote.pickupLatestTime) {
            dispatch(bookingActions.updateCollectionDetail({
                field: 'pickUpTo',
                value: quote.pickupLatestTime
            }))
        }
    }

    const [isSpotrate, setSpotrate] = useState<boolean>(false);

    const [isAwayService, setAwayService] = useState<boolean>(false);

    const [isDHLUKService, setDHLUKService] = useState<boolean>(false);

    const [showAwayCollectedModal, setShowAwayCollectedModal] = useState<boolean>(false);

    const onPreSelect = () => {
        if (customer.creditCheck.credit || (isWl && customer.bookingAllowed)) {
            if (isDHLUKService && !isAwayService && quote.serviceType === "COLLECTED" && booking.origin.value === "GB") {
                setShowAwayCollectedModal(true);
            } else {
                onSelect()
            }
        }
    }

    const onSelect = () => {
        dispatch(bookingActions.setSelectedQuote(quote));
        dispatch(bookingActions.updateField({
            field: 'insure',
            value: insured
        }));
        dispatch(bookingActions.updateTermsOfSale(ddpSelected ? TermsOfSale.DDP : TermsOfSale.DAP));
        setCollectionTimes(quote);
        dispatch(appDataActions.setQuotePageStatus(PageStatus.COMPLETE));
        history.push('/details');
    };

    const [isGoGreenPlus, setGoGreenPlus] = useState<boolean>(false);

    const setIsGoGreenPlus = (quote: QuoteObj) => {
        quote.charges.required.forEach(charge => {
            if (charge.type === "GOGREEN_PLUS") {
                setGoGreenPlus(true);
            }
        })
    }

    useEffect(() => {
        setIsGoGreenPlus(quote);
        setSpotrate(quote.carrierServiceName === "DHL-UK-EXT-ND-SP-DI" || quote.carrierServiceName === "DHL-UK-EXT-DOC-SP-DI");
        setAwayService(quote.carrierServiceName.endsWith("-AW"));
        setDHLUKService(quote.carrierServiceName.startsWith("DHL-UK"));
    }, []);

    return (
      <div>
          {showAwayCollectedModal && (
            <AMIModal
              className="quote__modal"
              title="Warning"
              width="50%"
              close={() => {
                  setShowAwayCollectedModal(false);
              }}
              buttons={[
                  <AMIButton
                    key={Math.random()}
                    variant='default'
                    size="large"
                    onClick={() => {
                        setShowAwayCollectedModal(false);
                        onSelect();
                    }}
                  >Confirm
                  </AMIButton>
              ]}
            >
                <p>
                    This service has a minimum requirement of 3 international shipments per postcode per day. If this requirement is not met, additional charges may apply.
                </p>
            </AMIModal>
          )}
          <div
            className="quote"
            onClick={onPreSelect}
          >
              <div className="quote__details">

                  <strong>{removeUnderscoresFromString(snakeCaseConvertor(quote.carrierProductCode))} {
                    isGoGreenPlus && <strong className="quote__details__go-green"> GO GREEN PLUS</strong>
                  }</strong>

                  <strong> {
                    isSpotrate && <strong className="quote__details__spotrate"> SPOTRATE</strong>
                  }</strong>

                  <strong> {
                    isAwayService && <strong className="quote__details__away"> AWAY COLLECTED</strong>
                  }</strong>

                  {showTime(quote) && <p> Estimated Delivery by {deliveryDate} {formatTime()}</p>}
                  {!showTime(quote) && <p> Estimated Delivery on {deliveryDate}</p>}

                  {showServiceType &&
                      <div className="quote__details__service-type">{snakeCaseConvertor(quote.serviceType)}</div>}

                  {quote.transportMethod === TransportMethod.EXPRESS && (
                    <div className="quote__details__transport-method">
                        <Icon
                          name="Express"
                          style={{
                              width: "24px"
                          }}
                        />
                        Express
                    </div>
                  )}

                  {quote.transportMethod === TransportMethod.DOMESTIC && (
                    <div className="quote__details__transport-method">
                        <Icon
                          name="Truck"
                          style={{
                              width: "24px"
                          }}
                        />
                        Domestic
                    </div>
                  )}

                  {quote.transportMethod === TransportMethod.ROAD && (
                    <div className="quote__details__transport-method">
                        <Icon
                          name="Truck"
                          style={{
                              width: "24px"
                          }}
                        />
                        Road
                        {getCarrierName(quote.quoteId) === "dhl" && (
                          <span
                            onMouseEnter={() => setIsRestrictionsPopoverVisible(true)}
                            onMouseLeave={() => setIsRestrictionsPopoverVisible(false)}
                          > <strong>(Restrictions
                <Icon
                  name="CircleQuestion"
                  color="var(--primary)"
                />
              )</strong>
                <AMIPopover isVisible={isRestrictionsPopoverVisible}>
                  Please note following prohibitions applicable only to road (ESI product) traffic: goods subject to excise taxes (tobacco, alcoholic beverages) & goods subject to government controls (drugs, medical devices and sanitary products, cosmetics, skincare and hygiene products, goods subject to phytosanitary or veterinary border controls, products for human consumption of all types, even being of non-animal origin, animal feeds.
                </AMIPopover>
              </span>
                        )}
                    </div>
                  )}
              </div>

              <div className="quote__price">
                  <p className="quote__price__total">{quoteCost}</p>
                  <div>
                      <div className="quote__price__breakdown">
                          <p>Base Price:</p>
                          <p>{currencySymbol}{basePrice.toFixed(2)}</p>
                      </div>
                      {quote.charges.required?.map((charge: any, index: number) => {
                          return (
                            <div key={charge.type + index} className="quote__price__breakdown">
                                <p>{snakeCaseConvertor(charge.type)}:</p>
                                <p>{currencySymbol}{charge.value.toFixed(2)}</p>
                            </div>
                          )
                      })}

                      {insured && (
                        <div className="quote__price__breakdown">
                            <p>Insurance:</p>
                            <p>{currencySymbol}{insuranceCost}</p>
                        </div>
                      )}

                      {ddpSelected && (
                        <div className="quote__price__breakdown">
                            <p>DDP:</p>
                            <p>{currencySymbol}{ddpCost}</p>
                        </div>
                      )}

                      {booking.shipperDetails.generateECS && (
                        <div className="quote__price__breakdown">
                            <p>{ecsData.type}</p>
                            <p>{currencySymbol}{ecsData.amount}</p>
                        </div>
                      )}

                      {getTradeRoute(customer, booking) === TradeRoute.DOMESTIC && booking.origin.value === 'AU' && quote.vat.value && (
                        <div className="quote__price__breakdown">
                            <p>GST:</p>
                            <p>{currencySymbol}{quote.vat.value}</p>
                        </div>
                      )}

                      {quote.discount.amount.value !== 0
                        && customer.countryOfResidence.value !== "GB"
                        && (
                          <div className="quote__price__breakdown">
                              <p>Discount:</p>
                              <p>{currencySymbol}{quote.discount.amount.value.toFixed(2)}</p>
                          </div>
                        )}
                  </div>
              </div>
          </div>
      </div>
    )
}

export default Quote;